import {AfterViewInit, Component, ElementRef, inject, NgZone, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {DatePipe, NgTemplateOutlet} from "@angular/common";
import {RouterLink} from "@angular/router";
import {GeneralHttpService} from "../http-requests/general-http.service";
import {PermissionService} from "../_auth/permission.service";
import {DashboardData, DashboardEvent} from "../models/stats/dashboard-data";
import {AppRoutes} from "../enums/routes";
import {LocationRoutes} from "../_modules/location/_models/_enums/location-routes";
import {CompanyRoutes} from "../_modules/company/x-models/x-enums/company-routes";
import {EventKind, VIRTUAL_EVENT_BADGE} from "../enums/event-kind";

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
  imports: [
    DatePipe,
    RouterLink,
    NgTemplateOutlet
  ]
})
export class HomeComponent implements OnInit, AfterViewInit, OnDestroy {
  /** The aggregated, non-sensitive dashboard payload (counts + live/upcoming/past events). */
  data?: DashboardData;
  loading = true;
  errored = false;

  /** Stamped once for the hero's date eyebrow. */
  readonly today = new Date();

  /** Elapsed % of the live event, computed once per load (a time-based binding would trip NG0100). */
  currentProgress = 0;

  /** Which timeline the small-screen slider shows (lg+ shows both side by side). */
  mobileTab: 'upcoming' | 'past' = 'upcoming';

  protected readonly Routes = AppRoutes;
  protected readonly LocationRoutes = LocationRoutes;
  protected readonly CompanyRoutes = CompanyRoutes;

  /**
   * #664 — the timeline rows chip the virtual kind; the hero never does, because it only ever carries a real
   * event (the server's `CurrentEvent` is real-only, and "läuft gerade" is a statement about an open till).
   *
   * The caption comes from {@link VIRTUAL_EVENT_BADGE} rather than a literal, so this pill, the events list,
   * the shift plan and the employee calendar cannot drift into three spellings of one word.
   */
  protected readonly EventKind = EventKind;
  protected readonly VirtualBadge = VIRTUAL_EVENT_BADGE;

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly zone = inject(NgZone);

  /** The shell's scrolling content box (found at runtime); drives the stats fade-out. */
  private scrollParent?: HTMLElement | null;
  private resizeObserver?: ResizeObserver;
  private rafPending = false;

  constructor(private generalHttpService: GeneralHttpService,
              public PermissionService: PermissionService) {
  }

  ngOnInit(): void {
    this.load();
  }

  /**
   * Wire up the scroll-driven effects:
   * - measure the (sticky) hero so the timeline column headers can pin right below it (`--hero-h`);
   * - fade/slide the stats out as the page scrolls.
   * Everything runs outside Angular and writes styles directly, so there's no change-detection churn.
   */
  ngAfterViewInit(): void {
    let el: HTMLElement | null = this.host.nativeElement.parentElement;
    while (el) {
      const oy = getComputedStyle(el).overflowY;
      if (oy === 'auto' || oy === 'scroll') break;
      el = el.parentElement;
    }
    this.scrollParent = el;
    this.zone.runOutsideAngular(() => {
      this.scrollParent?.addEventListener('scroll', this.onScroll, {passive: true});
      this.resizeObserver = new ResizeObserver(() => this.measureHero());
      this.resizeObserver.observe(this.host.nativeElement);
    });
  }

  ngOnDestroy(): void {
    this.scrollParent?.removeEventListener('scroll', this.onScroll);
    this.resizeObserver?.disconnect();
  }

  /** Tailwind's `sm` breakpoint — below it the dashboard is a plain, static, normal-scroll page (no effects). */
  private static readonly SM = 640;
  private static readonly LG = 1024;

  /**
   * Publish the hero's rendered height as a CSS var so the sticky column headers can offset by it, and
   * re-apply the scroll effects (covers crossing the `sm` breakpoint on resize).
   */
  private measureHero(): void {
    const hero = this.host.nativeElement.querySelector<HTMLElement>('[data-hero]');
    this.host.nativeElement.style.setProperty('--hero-h', (hero?.offsetHeight ?? 0) + 'px');
    this.applyScrollFx();
  }

  private readonly onScroll = (): void => {
    if (this.rafPending) return;
    this.rafPending = true;
    requestAnimationFrame(() => {
      this.rafPending = false;
      this.applyScrollFx();
    });
  };

  /** Fade + drift the stats with scroll — but only at `sm`+; below small it stays plain (no scroll effect). */
  private applyScrollFx(): void {
    const stats = this.host.nativeElement.querySelector<HTMLElement>('[data-stats]');
    if (!stats) return;
    const sp = this.scrollParent;
    if (!sp || window.innerWidth < HomeComponent.LG) {
      stats.style.opacity = '';
      stats.style.transform = '';
      stats.style.pointerEvents = '';
      return;
    }
    const y = sp.scrollTop;
    const fade = Math.max(0, 1 - y / 110);                   // fully gone after ~110px of scroll
    stats.style.opacity = String(fade);
    stats.style.transform = `translateY(${-Math.min(y, 90) * 0.35}px)`;
    stats.style.pointerEvents = fade < 0.05 ? 'none' : '';
  }

  /** (Re)load the dashboard payload, tracking loading/error so the template can show the right state. */
  load(): void {
    this.loading = true;
    this.errored = false;
    this.generalHttpService.getDashboardData().subscribe({
      next: data => {
        this.data = data;
        this.currentProgress = data.currentEvent ? this.eventProgress(data.currentEvent) : 0;
        this.loading = false;
        // Re-measure once the hero is in the DOM (next frame).
        requestAnimationFrame(() => this.measureHero());
      },
      error: () => {
        this.errored = true;
        this.loading = false;
      }
    });
  }

  /** Whether event rows should deep-link into the event-details page (management read permission). */
  get canOpenEvents(): boolean {
    return this.PermissionService.CheckPermission(this.PermissionService.CombinedPermission_EventGetExtendedEvent());
  }

  /** Whether to show the "Alle anzeigen" links to the full events list. */
  get canViewAllEvents(): boolean {
    return this.PermissionService.CheckPermission(this.PermissionService.Permissions_EventsComponent());
  }

  /**
   * Whether the user may open the shift maker — the ShardAdmin/owner audience plus a `ShiftLead` (#280).
   *
   * Deliberately the **same** `PermissionSet` the navbar entry and the `/company/shiftmaker` route are driven
   * by, so the quick link shows exactly when the route would resolve. That is also why a shift lead now sees
   * it: home is the landing page of the management shell it enters, and this tile is the only link a
   * shift-lead home has to offer. Giving the tile its own condition would let it drift from the door it opens
   * — which is the precise failure #280 shipped with.
   */
  get canPlanShifts(): boolean {
    return this.PermissionService.CheckPermission(this.PermissionService.Permissions_ShiftMakerComponent());
  }

  /** Elapsed fraction (0–100) of the live event, used to fill the progress bar in the "live now" section. */
  eventProgress(event: DashboardEvent): number {
    const start = new Date(event.start).getTime();
    const end = new Date(event.end).getTime();
    const now = Date.now();
    if (!isFinite(start) || !isFinite(end) || end <= start) return 0;
    return Math.min(100, Math.max(0, ((now - start) / (end - start)) * 100));
  }
}
